import { create } from 'zustand'

import type {
  ApiErrorDetail,
  AvailableFiltersType,
  CharacteristicFilterType,
  EntityFilterType,
  ServerChosenFilters,
  StoreShortWithSupplierType,
} from '@/types'
import { ignoreError } from '@/utils/api/errors/ignoreError'

export type SearchErrorType =
  | string
  | { message: string; details?: ApiErrorDetail[]; traceId?: string }
  | null

export interface InitialFilters {
  searchTerm: string
  categories: EntityFilterType[]
  brands: EntityFilterType[]
  productCodes: number[]
  parents: EntityFilterType[]
}

interface SearchStateInterface {
  currentlySearchableParents: EntityFilterType[]
  withDiscontinued: boolean
  matchUniformSlottedCharacteristics: boolean
  searchTerm: string
  currentlySearchableCategories: EntityFilterType[]
  currentlySearchableBrands: EntityFilterType[]
  currentlySearchableStores: StoreShortWithSupplierType[]
  currentlySearchableCharacteristics: CharacteristicFilterType[]
  currentlySearchableNomenclatureGroupId: string
  availableFilters: AvailableFiltersType
  currentlySearchableProductCodes: number[]
  serverChosenFilters?: ServerChosenFilters | null
  withLeftovers: boolean
  searchError: SearchErrorType

  initialFilters: InitialFilters | null

  setSearchTerm: (searchTerm: string) => void
  setCurrentlySearchableCategories: (
    currentlySearchableCategories: EntityFilterType[]
  ) => void
  setCurrentlySearchableProductCodes: (codes: number[]) => void
  setCurrentlySearchableBrands: (
    currentlySearchableBrands: EntityFilterType[]
  ) => void
  setCurrentlySearchableStores: (
    selectedStores: StoreShortWithSupplierType[]
  ) => void
  setCurrentlySearchableCharacteristics: (
    currentlySearchableCharacteristics: CharacteristicFilterType[]
  ) => void
  setCurrentlySearchableNomenclatureGroupId: (
    currentlySearchableNomenclatureGroupId: string
  ) => void
  setAvailableFilters: (availableFilters: AvailableFiltersType) => void
  setServerChosenFilters: (filters: ServerChosenFilters | null) => void
  setWithLeftovers: (withLeftovers: boolean) => void
  setSearchError: (searchError: SearchErrorType) => void
  setCurrentlySearchableParents: (parents: EntityFilterType[]) => void
  setWithDiscontinued: (val: boolean) => void
  setMatchUniformSlottedCharacteristics: (val: boolean) => void

  setInitialFilters: (filters: InitialFilters | null) => void

  setInitialSearchStore: () => void
}

const INITIAL_FILTERS_STORAGE_KEY = 'pips_initial_filters'

function loadInitialFiltersFromStorage(): InitialFilters | null {
  try {
    const raw = localStorage.getItem(INITIAL_FILTERS_STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function saveInitialFiltersToStorage(filters: InitialFilters | null) {
  try {
    if (filters === null) {
      localStorage.removeItem(INITIAL_FILTERS_STORAGE_KEY)
    } else {
      localStorage.setItem(INITIAL_FILTERS_STORAGE_KEY, JSON.stringify(filters))
    }
  } catch {
    ignoreError()
  }
}

const initialState = {
  searchTerm: '',
  currentlySearchableCategories: [],
  currentlySearchableBrands: [],
  currentlySearchableStores: [],
  currentlySearchableProductCodes: [],
  currentlySearchableCharacteristics: [],
  currentlySearchableParents: [],
  currentlySearchableNomenclatureGroupId: '',
  availableFilters: {} as AvailableFiltersType,
  withDiscontinued: true,
  matchUniformSlottedCharacteristics: false,
  withLeftovers: false,
  searchError: null,
  initialFilters: loadInitialFiltersFromStorage(),
}

export const useSearchStore = create<SearchStateInterface>((set) => ({
  ...initialState,

  setSearchTerm: (searchTerm: string) => set(() => ({ searchTerm })),
  setCurrentlySearchableCategories: (
    currentlySearchableCategories: EntityFilterType[]
  ) => set(() => ({ currentlySearchableCategories })),
  serverChosenFilters: undefined,
  setServerChosenFilters: (serverChosenFilters) => set({ serverChosenFilters }),
  setCurrentlySearchableBrands: (
    currentlySearchableBrands: EntityFilterType[]
  ) => set(() => ({ currentlySearchableBrands })),
  setCurrentlySearchableStores: (
    currentlySearchableStores: StoreShortWithSupplierType[]
  ) => set(() => ({ currentlySearchableStores })),
  setCurrentlySearchableProductCodes: (
    currentlySearchableProductCodes: number[]
  ) => set(() => ({ currentlySearchableProductCodes })),

  setCurrentlySearchableCharacteristics: (
    currentlySearchableCharacteristics: CharacteristicFilterType[]
  ) => set(() => ({ currentlySearchableCharacteristics })),
  setCurrentlySearchableNomenclatureGroupId: (
    currentlySearchableNomenclatureGroupId: string
  ) => set(() => ({ currentlySearchableNomenclatureGroupId })),
  setAvailableFilters: (availableFilters: AvailableFiltersType) =>
    set(() => ({ availableFilters })),
  setWithLeftovers: (withLeftovers: boolean) => set(() => ({ withLeftovers })),
  setSearchError: (searchError: SearchErrorType) =>
    set(() => ({ searchError })),
  setCurrentlySearchableParents: (currentlySearchableParents) =>
    set({ currentlySearchableParents }),
  setWithDiscontinued: (withDiscontinued) => set({ withDiscontinued }),
  setMatchUniformSlottedCharacteristics: (matchUniformSlottedCharacteristics) =>
    set({ matchUniformSlottedCharacteristics }),

  setInitialFilters: (initialFilters: InitialFilters | null) => {
    saveInitialFiltersToStorage(initialFilters)
    set({ initialFilters })
  },

  setInitialSearchStore: () => {
    saveInitialFiltersToStorage(null)
    set({ ...initialState, initialFilters: null })
  },
}))
